import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Shield, AlertTriangle, Truck, CreditCard, FileText, XCircle, User, Phone, Mail, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../mock';

export const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => navigate('/book')}
            variant="outline"
            className="mb-8 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Booking</span>
          </Button>

          <Card className="shadow-xl border-0 bg-white">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg">
                    AIS
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900">Terms & Conditions</h1>
                </div>
                <p className="text-slate-600">AIS Express Same-Day Delivery Services</p>
                <p className="text-sm text-slate-500 mt-2">Last Updated: 24/10/2025</p>
              </div>

              <div className="prose prose-slate max-w-none">
                
                <p className="text-slate-700 leading-relaxed mb-8">
                  Welcome to AIS Express. By using our delivery services, you agree to the following Terms and Conditions. Please read them carefully.
                </p>

                {/* 1. Company Information */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-emerald-600" />
                    1. Company Information
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    AIS Express ("we", "our", or "us") provides collection and delivery services within the United Kingdom.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    We deliver all items except prohibited or dangerous goods such as chemicals, explosives, weapons, toxic substances, or any items restricted by UK law.
                  </p>
                </section>

                {/* 2. Use of Our Service */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-emerald-600" />
                    2. Use of Our Service
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    By using our service, you confirm that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>You are at least 18 years old, or acting with legal permission.</li>
                    <li>The items you request us to deliver are legal and safe for transport.</li>
                    <li>You provide accurate delivery details (names, addresses, instructions).</li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed mt-4">
                    We are not responsible for delivery failures caused by incorrect or incomplete information provided by you.
                  </p>
                </section>

                {/* 3. Prohibited Items */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                    3. Prohibited Items
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We do not accept or deliver the following items:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>Dangerous chemicals or hazardous materials</li>
                    <li>Illegal drugs or substances</li>
                    <li>Weapons or explosive devices</li>
                    <li>Items requiring special licensing or temperature control</li>
                    <li>Any goods restricted by UK postal/regulatory laws</li>
                  </ul>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                    <p className="text-red-800 font-medium">
                      If a prohibited item is discovered, we may refuse or cancel the delivery without refund or liability.
                    </p>
                  </div>
                </section>

                {/* 4. Ordering & Delivery */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Truck className="h-5 w-5 mr-2 text-emerald-600" />
                    4. Ordering & Delivery
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Orders can be placed through our website, phone, or other communication channels.
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Delivery times are estimates and may vary due to traffic, weather, or other conditions.
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-2">
                    If no one is available at the delivery address, we may:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                    <li>Attempt re-delivery (additional charges may apply), or</li>
                    <li>Return the item to the sender.</li>
                  </ul>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Waiting Time Policy
                    </h3>
                    <p className="text-amber-800 mb-2">
                      Our drivers will wait at the collection or delivery address to complete the service:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-amber-800">
                      <li><strong>First 30 minutes:</strong> Free of charge</li>
                      <li><strong>After 30 minutes:</strong> An additional fee of <strong>£30 per hour</strong> (pro-rated per minute) will be charged to the customer</li>
                    </ul>
                    <p className="text-amber-800 mt-3">
                      Waiting time charges apply when delays are caused by the customer (e.g., items not ready for collection, recipient unavailable, access issues). Please ensure all items are ready and personnel are available at the scheduled time to avoid additional charges.
                    </p>
                  </div>
                </section>

                {/* 5. Payment */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2 text-emerald-600" />
                    5. Payment
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We do not accept online payment through the website.
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Customers will receive an invoice, which must be paid within the payment terms stated on the invoice.
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-2">
                    Failure to pay may result in:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>Additional late fees</li>
                    <li>Suspension of future delivery services</li>
                    <li>Legal collection action if necessary</li>
                  </ul>
                </section>

                {/* 6. Insurance & Liability */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-emerald-600" />
                    6. Insurance & Liability
                  </h2>
                  
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4">
                    <h3 className="font-semibold text-emerald-900 mb-2">Goods in Transit Insurance</h3>
                    <p className="text-emerald-800">
                      All deliveries are covered by our <strong>£50,000 Goods in Transit Insurance</strong>, providing comprehensive protection for your items during transportation.
                    </p>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Limit of Liability</h3>
                    <p className="text-blue-800">
                      Our maximum liability is capped at <strong>£5,000,000</strong> per incident, subject to the terms and conditions outlined in this agreement.
                    </p>
                  </div>

                  <p className="text-slate-700 leading-relaxed mb-4">
                    We take reasonable care when handling all items. However:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>We are not liable for items that were improperly packaged, fragile, or had concealed damage before we received them.</li>
                    <li>Claims for damaged or lost items must be made within 24 hours of delivery, and may require proof (photos, damage report, etc.).</li>
                    <li>Insurance claims are subject to verification and must be supported by appropriate documentation.</li>
                    <li>The declared value of goods must be accurate at the time of booking.</li>
                  </ul>
                </section>

                {/* 7. Cancellation Policy */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <XCircle className="h-5 w-5 mr-2 text-emerald-600" />
                    7. Cancellation Policy
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Orders may be cancelled before collection at no charge.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    If a driver has already been dispatched, a cancellation fee may apply to cover time and travel costs.
                  </p>
                </section>

                {/* 8. Personal Information */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-emerald-600" />
                    8. Personal Information
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We do not require users to create accounts on our website.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    Any personal information provided (such as address or contact number) is used only for delivery and invoicing purposes and will not be shared with third parties except when legally required.
                  </p>
                </section>

                {/* 9. Changes to These Terms */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-emerald-600" />
                    9. Changes to These Terms
                  </h2>
                  <p className="text-slate-700 leading-relaxed">
                    We may update these Terms and Conditions occasionally. The updated version will be available on our website with the revised date.
                  </p>
                </section>

                {/* 10. Contact Us */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-emerald-600" />
                    10. Contact Us
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    For questions or assistance, please contact us at:
                  </p>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Email: <a href={`mailto:${contactInfo.email}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.email}</a></span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Phone: <a href={`tel:${contactInfo.phone}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.phone}</a></span>
                    </div>
                  </div>
                </section>

              </div>

              {/* Agreement Footer */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-600 text-center">
                  By using AIS Express services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
